import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameDay,
  isWithinInterval,
  startOfWeek,
  addDays,
  isAfter,
  isBefore,
  startOfDay,
  endOfDay,
  addMonths,
} from 'date-fns'
import css from './calendar.module.css'
import cn from '../../../../../utils/cn.ts'

interface IProps {
  onDateSelect: (date: Date) => void
  startDate?: Date
  endDate?: Date
  currentMonth: Date
  range?: boolean
  double?: boolean
  handlePrevMonth: () => void
  handleNextMonth: () => void
}

const Calendar = ({
  onDateSelect,
  startDate,
  endDate,
  currentMonth,
  range = false,
  double = false,
  handlePrevMonth,
  handleNextMonth,
}: IProps) => {
  const secondMonth = addMonths(currentMonth, 1)
  const isDisabled = (date: Date, month: Date) =>
    !isWithinInterval(date, {
      start: startOfMonth(month),
      end: endOfMonth(month),
    })

  const handleDateClick = (date: Date, month: Date) => {
    if (isDisabled(date, month)) {
      date < startOfMonth(month) ? handlePrevMonth() : handleNextMonth()
    }
    onDateSelect(date)
  }

  const isSelected = (date: Date) => {
    if (!startDate || range) return false

    if (range && startDate && endDate) {
      return isWithinInterval(date, { start: startDate, end: endDate })
    }
    return isSameDay(date, startDate)
  }

  const generateDaysInMonth = (month: Date) => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 })
    const end = addDays(start, 41)

    return eachDayOfInterval({
      start,
      end,
    })
  }

  const isRangeStart = (date: Date) => range && startDate && isSameDay(startOfDay(date), startOfDay(startDate))
  const isRangeEnd = (date: Date) => range && endDate && isSameDay(endOfDay(date), endOfDay(endDate))
  const isToday = (date: Date) => isSameDay(date, new Date())
  const isInRange = (date: Date) => range && startDate && endDate && isAfter(date, startDate) && isBefore(date, endDate)

  const renderCalendar = (month: Date, double = false) => {
    const daysInMonth = generateDaysInMonth(month)

    return (
      <div className={`${css.calendar} ${double && css.doubleCalendar}`}>
        <div className={css.daysHeader}>
          {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
            <div key={day} className={css.dayName}>
              {day}
            </div>
          ))}
        </div>
        <div className={css.daysGrid}>
          {daysInMonth.map((day) => (
            <div className={css.wrapperDay} key={day.toISOString()}>
              <div
                className={cn({
                  [css.day]: true,
                  [css.today]: isToday(day),
                  [css.selected]: isSelected(day),
                  [css.rangeStart]: isRangeStart(day),
                  [css.rangeEnd]: isRangeEnd(day),
                  [css.inRange]: isInRange(day),
                  [css.disabled]: isDisabled(day, month),
                })}
                onClick={() => handleDateClick(day, month)}
              >
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={css.calendarContainer}>
      {renderCalendar(currentMonth)}
      {double && renderCalendar(secondMonth, true)}
    </div>
  )
}

export default Calendar
